import { useEffect, useState, useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import PageLoading from '@zenbusiness/zui/PageLoading';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import { orderBy } from 'lodash';

import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useFormStep from '#client/hooks/useFormStep';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useOpenAstraModal from '#client/hooks/useOpenAstraModal';
import { checkCardErrors } from '#client/utils/cards';

import { DialogContentStyled, Wrapper } from './DialogAndWrapper.styles';

import { MODAL_ACTION, CARD_ERROR_MESSAGES } from '../constants/instants';

export default function CheckCardError() {
  const [hasCardError, setHasCardError] = useState(false);
  const [cardErrorInformation, setCardErrorInformation] = useState({});
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { loading: astraProfileLoading, cardList } = useAstraProfileContext();
  const { getAndRedirectAstraUrl } = useOpenAstraModal();
  const { depositData, dispatchDepositData } = useFormStep();
  const { createAstraCardError } = depositData;

  const hasMoreThanOneCard = cardList?.length >= 1;
  const hasLimitCards = cardList?.length >= 3;
  const cardToCheck = useMemo(() => {
    // If user can not create a new astra card, return null
    // Otherwise, take the last created card
    if (!isUndefinedOrNull(createAstraCardError)) {
      return null;
    }

    if (astraProfileLoading || !cardList || cardList?.length === 0) {
      return null;
    }

    let foundCard;

    const shortedCardList = orderBy(cardList ?? [], ['cardCreatedAt'], ['desc']);

    if (depositData?.selectedFromAccount?.uuid) {
      foundCard = shortedCardList.find(({ uuid }) => uuid === depositData.selectedFromAccount.uuid);
    } else {
      [foundCard] = shortedCardList;
    }

    return foundCard || null;
  }, [astraProfileLoading, cardList, createAstraCardError, depositData.selectedFromAccount.uuid]);

  const buttonLabel = cardErrorInformation?.buttonLabel || 'Try another card';

  const buttonTryAnotherCardPressed = useButtonPressedCallback({
    elementName: 'add_card_again_instantdep',
    elementText: buttonLabel,
    appPageName: cardErrorInformation?.screenName
  });

  const buttonUseSavedCardPressed = useButtonPressedCallback({
    elementName: 'use_saved_card_instantdep',
    elementText: 'Use other saved card',
    appPageName: cardErrorInformation?.screenName
  });

  const handleTryAnotherCard = () => {
    buttonTryAnotherCardPressed();

    getAndRedirectAstraUrl({ isClearAllModal: true });
  };

  const handleUseSavedCard = () => {
    buttonUseSavedCardPressed();

    // Go to enter amount screen and remove create card error
    dispatchDepositData({
      type: MODAL_ACTION.GO_TO_WHICH_CARD_FORM,
      payload: {
        createAstraCardError: null
      }
    });
  };

  useEffect(() => {
    // If create astra card failed, show common error screen
    if (!isUndefinedOrNull(createAstraCardError)) {
      setHasCardError(true);
      setCardErrorInformation(CARD_ERROR_MESSAGES.COULD_NOT_PROCESS_WITH_THIS_CARD);
      return;
    }

    // If create astra card successfully, check card is valid via checkCardErrors function
    if (!astraProfileLoading && cardList?.length > 0 && cardToCheck) {
      const cardError = checkCardErrors(cardToCheck);

      if (cardError) {
        setHasCardError(true);
        setCardErrorInformation(cardError);
      } else {
        dispatchDepositData({
          type: MODAL_ACTION.GO_TO_HOW_MUCH_FORM,
          payload: {
            createAstraCardError: null
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [astraProfileLoading, cardList, cardToCheck]);

  useModalDisplayTracker({
    elementName: cardErrorInformation?.screenName,
    elementText: cardErrorInformation?.screenName,
    appPageName: 'active_banking_dashboard'
  });

  if (!hasCardError) {
    return <PageLoading container />;
  }

  return (
    <DialogContentStyled role="form">
      <ContainerGrid>
        <FocusGrid>
          <Wrapper gap={{ xs: '32px', md: '40px' }}>
            <Stack
              alignItems="center"
              justifyContent="center"
            >
              <Box>
                <SharedAsset
                  alt=""
                  height={isDesktop ? 200 : 110}
                  src="/s2/vector/alert-green-iso.svg"
                  width={isDesktop ? 200 : 110}
                />
              </Box>
              <Stack
                gap="8px"
                mt={{ xs: '24px', md: '40px' }}
              >
                <Heading
                  align="center"
                  size="3xl"
                  weight="bold"
                >
                  {cardErrorInformation.errorMessage}
                </Heading>
                <Text
                  align="center"
                  color="tertiary"
                  size="lg"
                >
                  {cardErrorInformation.errorDescription}
                </Text>
              </Stack>
              <Stack
                alignItems="center"
                justifyContent="center"
                mt={{ xs: '32px', md: '40px' }}
              >
                {!hasLimitCards && (
                  <LoadingButton
                    color="primary"
                    fullWidth
                    onClick={handleTryAnotherCard}
                    size="large"
                    variant="contained"
                  >
                    {buttonLabel}
                  </LoadingButton>
                )}
                {hasMoreThanOneCard && (
                  <LoadingButton
                    fullWidth
                    onClick={handleUseSavedCard}
                    size="large"
                    variant="text"
                  >
                    Use other saved card
                  </LoadingButton>
                )}
              </Stack>
            </Stack>
          </Wrapper>
        </FocusGrid>
      </ContainerGrid>
    </DialogContentStyled>
  );
}
